import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronDown, Mic, Paperclip, Send, Square, X } from "lucide-react";

import { Chat, type AskDraft, type ChatContext, type ChatMessage } from "@/api/chat";
import { fetchBlob } from "@/api/client";
import { Comments, Me, Users, type CommentPatch } from "@/api/resources";
import type { CommentOut } from "@/api/types";
import { isSystemNotice, SystemNotice } from "@/components/TaskHistory";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { Chip, useIsDesktop } from "@/kit";
import { askOf, EMPTY_ASK, MAX_ACTION, MAX_BLOCKING_NOTE, type AskForm } from "@/lib/ask";
import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENT_MB } from "@/lib/limits";
import { formatFileSize } from "@/lib/format";
import { cn } from "@/lib/cn";
import { Markdown } from "@/lib/markdown";
import { firstNewId, markTarget, withNewLine, type ReadMark } from "@/lib/unread";

function MessageFile({ file }: { file: ChatMessage["attachments"][number] }) {
  const [url, setUrl] = useState<string>();
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const image = /^image\/(jpeg|png|webp|gif)$/.test(file.mime);
  const audio = file.mime.startsWith("audio/") || /\.(ogg|opus|mp3|m4a|wav|webm)$/i.test(file.original_name);
  const open = async () => {
    setBusy(true);
    try {
      const next = URL.createObjectURL(await fetchBlob(`/api/v1/attachments/${file.id}`));
      if (image || audio) setUrl(next);
      else {
        const link = document.createElement("a");
        link.href = next; link.download = file.original_name; link.click();
        window.setTimeout(() => URL.revokeObjectURL(next), 30_000);
      }
    } catch (error) { toast.error(error instanceof Error ? error.message : "Файл недоступен"); }
    finally { setBusy(false); }
  };
  return <div className="my-2 rounded-compact border border-p-border p-3">
    <div className="flex items-center gap-1.5 break-all text-meta"><Paperclip className="h-4 w-4 shrink-0 text-p-text-muted" aria-hidden="true" />{file.original_name} · {formatFileSize(file.size)}</div>
    {url && image ? <img src={url} alt={file.original_name} className="mt-2 max-h-80 max-w-full rounded-lg object-contain" />
      : url && audio ? <audio src={url} controls className="mt-2 w-full" />
        : <Button size="compact" variant="ghost" loading={busy} onClick={() => void open()}>{audio ? "Слушать" : image ? "Показать" : "Скачать"}</Button>}
    {file.transcription_status === "pending" && <p className="text-meta text-hint">Расшифровывается… Оригинал сохранён.</p>}
    {file.transcription_status === "done" && <div className="mt-2 whitespace-pre-wrap text-body"><span className="text-meta text-hint">Расшифровка Whisper</span><p>{file.transcript || "Речь не распознана"}</p></div>}
    {file.transcription_status === "failed" && <Button size="compact" variant="ghost" onClick={() => {
      void Chat.transcribe(file.id).then(() => qc.invalidateQueries({ queryKey: ["chat"] })).catch(() => toast.error("Не удалось повторить расшифровку"));
    }}>Повторить расшифровку</Button>}
  </div>;
}

/**
 * Spec 016: an agent's long message reads as its short version first.
 * Spec 020 D6: both versions render as Markdown, never as HTML.
 */
export function MessageText({ message, full, onToggle }: { message: Pick<CommentOut, "body_md" | "summary_md" | "edited_at">; full: boolean; onToggle: () => void }) {
  const edited = message.edited_at && <span className="text-xs text-hint">изменено</span>;
  if (!message.summary_md) {
    return <div className="mt-2 text-body"><Markdown text={message.body_md} />{edited}</div>;
  }
  return <div className="mt-2 space-y-1">
    {!full && <span className="text-xs text-hint">Кратко</span>}
    <Markdown className="text-body" text={full ? message.body_md : message.summary_md} />
    {edited}
    <button type="button" className="text-meta text-accent" aria-expanded={full} onClick={onToggle}>
      {full ? "Кратко" : "Полностью"}
    </button>
  </div>;
}

/**
 * Spec 016: what an edit changes. An untouched text is not resent, so a
 * whitespace difference cannot drop the short version, and a message with a
 * short version always sends the one from the form, so it stays with the text.
 */
export function editPatch(message: Pick<CommentOut, "body_md" | "summary_md">, text: string, summary: string): CommentPatch {
  const patch: CommentPatch = {};
  if (text.trim() !== message.body_md.trim()) patch.body_md = text.trim();
  if (message.summary_md != null && (patch.body_md !== undefined || summary.trim() !== message.summary_md.trim())) {
    patch.summary_md = summary.trim() || null;
  }
  return patch;
}

/**
 * Spec 020 D7: how many messages arrived below the one that was newest before.
 * Older pages loaded above never count; if that newest message is gone
 * (deleted), what is newest now counts as one arrival.
 */
export function arrivals(ids: string[], previousNewest: string | undefined): number {
  if (!previousNewest || !ids.length || ids[ids.length - 1] === previousNewest) return 0;
  const at = ids.lastIndexOf(previousNewest);
  return at < 0 ? 1 : ids.length - 1 - at;
}

/**
 * Whether older pages may load by themselves as the reader nears the top.
 * Not while a linked message (`?message=`) is shown in its own frame above the
 * feed: the frame sits by the loading edge, each page pulled the screen away
 * and the next page loaded — the message was lost (PR #13 review, blocker 2).
 * The «Показать более ранние» button still works.
 */
export function mayLoadOlderByItself(focusId: string | undefined, ids: string[]): boolean {
  return !focusId || ids.includes(focusId);
}

/** Where a message of the whole feed belongs; a case's title already reads «Кейс №N. …» (Spec 025). */
export function contextLabel(message: Pick<ChatMessage, "parent_type" | "context_title">): string {
  const title = message.context_title;
  if (message.parent_type === "case") return title.startsWith("Кейс") ? title : `Кейс: ${title}`;
  return `${message.parent_type === "task" ? "Задача" : message.parent_type === "idea" ? "Идея" : "Материал"}: ${title}`;
}

/**
 * PC audit 28.09, point 21 (Kostya): in a discussion the newest messages sat at
 * the very bottom. A switch on top: «Сначала новые» / «Сначала старые». By
 * default a computer shows the newest first and a phone the oldest first
 * (agreed by Kostya and Sanya, 28.09); the choice is remembered on the device.
 * The full chat screen (`pinToLatest`) stays a messenger: oldest on top.
 */
export type ChatOrder = "newest" | "oldest";
const ORDER_KEY = "prosto.chat.order";

export function defaultChatOrder(desktop: boolean): ChatOrder {
  return desktop ? "newest" : "oldest";
}

function storedChatOrder(): ChatOrder | null {
  try {
    const value = window.localStorage.getItem(ORDER_KEY);
    return value === "newest" || value === "oldest" ? value : null;
  } catch {
    return null; // private mode or no storage: the device default
  }
}

/** The feed in reading order: by time, the other way round when the newest come first. */
export function orderFeed<T extends { at: string; id: string }>(items: T[], order: ChatOrder): T[] {
  const byTime = [...items].sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
  return order === "newest" ? byTime.reverse() : byTime;
}

/**
 * Task a60d4d9a: a thin line «Новое» where the reader stopped last time — before the first
 * unread message, or after it when the newest come first (then the new ones are above).
 */
export function NewLine({ newestFirst }: { newestFirst: boolean }) {
  return <div role="separator" aria-label={newestFirst ? "Выше — новые сообщения" : "Ниже — новые сообщения"}
    className="flex items-center gap-2 text-chip font-semibold text-p-needs">
    <span className="h-px flex-1 bg-p-needs" aria-hidden="true" />
    <span>{newestFirst ? "Новое ↑" : "Новое"}</span>
    <span className="h-px flex-1 bg-p-needs" aria-hidden="true" />
  </div>;
}

const NEAR_BOTTOM = 120; // px: closer than this reads as «at the newest»
const FAR_FROM_BOTTOM = 600; // px: further than this, offer «вниз» even with nothing new

/**
 * `pinToLatest` is the full chat screen (Spec 020 D7): it opens on the newest
 * message, follows new ones while you are at the bottom, counts them on «вниз»
 * when you have scrolled up, and keeps your place while older pages load above.
 * A discussion inside a task leaves the page where it is.
 */
/**
 * The message field of a thread — Spec 021, PR 021 review (Kostya's agent, 27.09).
 *
 * Its own component with its own state: a key typed here re-renders this field
 * only, never the feed above (a full feed re-render on every key made the phone
 * re-pull the field to the keyboard — the screen jumped). It grows with its text
 * through a hidden mirror in the same grid cell (`.grow-wrap`), never through
 * `height: auto`, which collapsed it to one line on every key first.
 *
 * Spec 034: «Запросить ответ» makes the message an addressed request (Spec 033) — whom,
 * what exactly, a personal answer or not, whether the work stands. It goes with words only.
 */
function ChatComposer({ context, reply, onCancelReply, onSent, placeholder, sticky }: {
  context: ChatContext;
  reply: ChatMessage | null;
  onCancelReply: () => void;
  onSent: () => void;
  placeholder: string;
  sticky: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const mounted = useRef(true);
  const attempt = useRef<{ signature: string; key: string }>();
  const [asking, setAsking] = useState(false);
  const [askForm, setAskForm] = useState<AskForm>(EMPTY_ASK);
  // Not usePeople(): the thread's tests double its own boundaries, not lib/people's.
  const people = useQuery({ queryKey: ["users"], queryFn: () => Users.list(), staleTime: 10 * 60_000 });
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
      if (recorder.current?.state === "recording") recorder.current.stop();
      stream.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);
  const selectFiles = (incoming: File[]) => {
    setFiles((current) => {
      const next = [...current, ...incoming];
      if (next.length > 10 || next.reduce((n, f) => n + f.size, 0) > MAX_ATTACHMENT_BYTES) {
        toast.error(`До 10 файлов, суммарно до ${MAX_ATTACHMENT_MB} МБ за отправку`);
        return current;
      }
      return next;
    });
  };
  const record = async () => {
    if (recording) { recorder.current?.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Запись здесь недоступна. Прикрепи аудиофайл или отправь голосовое через бота."); return;
    }
    try {
      const acquired = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mounted.current) { acquired.getTracks().forEach((track) => track.stop()); return; }
      stream.current = acquired;
      const mime = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"].find((t) => MediaRecorder.isTypeSupported(t));
      const next = new MediaRecorder(acquired, mime ? { mimeType: mime } : undefined);
      recorder.current = next;
      const chunks: Blob[] = [];
      let total = 0;
      next.ondataavailable = (event) => {
        chunks.push(event.data); total += event.data.size;
        if (total >= MAX_ATTACHMENT_BYTES && next.state === "recording") next.stop();
      };
      next.onstop = () => {
        clearTimeout(timer.current);
        acquired.getTracks().forEach((track) => track.stop());
        if (!mounted.current) return;
        setRecording(false);
        const type = next.mimeType || "audio/webm";
        const extension = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File(chunks, `voice-${Date.now()}.${extension}`, { type });
        if (file.size) selectFiles([file]);
      };
      next.start(1000); setRecording(true);
      timer.current = setTimeout(() => { if (next.state === "recording") next.stop(); }, 300_000);
    } catch { stream.current?.getTracks().forEach((track) => track.stop()); toast.error("Нет доступа к микрофону. Разреши его или прикрепи аудиофайл."); }
  };
  const send = async () => {
    if (busy || recording || (!draft.trim() && !files.length && !asking)) return;
    let ask: AskDraft | undefined;
    if (asking) {
      if (files.length) { toast.error("Запрос уходит без файлов: убери их или отправь отдельным сообщением"); return; }
      const made = askOf(askForm);
      if ("error" in made) { toast.error(made.error); return; }
      ask = made.ask;
    }
    const body = {
      ...context,
      body_md: draft.trim() || (ask ? ask.action : ""),
      ...(reply ? { reply_to_id: reply.id } : {}),
      ...(ask ? { ask } : {}),
    };
    const signature = JSON.stringify([body, files.map((f) => [f.name, f.size, f.lastModified])]);
    if (attempt.current?.signature !== signature) attempt.current = { signature, key: crypto.randomUUID() };
    setBusy(true);
    try {
      if (files.length) await Chat.media(body, files, attempt.current.key);
      else await Chat.send(body, attempt.current.key);
      setDraft(""); setFiles([]); attempt.current = undefined;
      setAsking(false); setAskForm(EMPTY_ASK);
      onSent();
      await qc.invalidateQueries({ queryKey: ["chat"] });
      await qc.invalidateQueries({ queryKey: ["comments"] });
      if (ask) {
        void qc.invalidateQueries({ queryKey: ["overview"] });
        void qc.invalidateQueries({ queryKey: ["task"] });
      }
    } catch (error) { toast.error(error instanceof Error ? error.message : "Не удалось отправить. Текст и файлы сохранены для повтора."); }
    finally { setBusy(false); }
  };
  const hasText = Boolean(draft.trim()) || files.length > 0 || (asking && Boolean(askForm.action.trim()));
  const field = "h-11 w-full rounded-compact border border-p-border bg-p-surface px-3 text-[16px] text-p-text placeholder:text-p-text-muted";
  const setAsk = (patch: Partial<AskForm>) => setAskForm((current) => ({ ...current, ...patch }));
  return <div className={cn(
    "flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-3 py-2",
    // The band under the floating field is page background, so no message shows through it.
    sticky && "sticky bottom-[max(env(safe-area-inset-bottom),8px)] z-sticky shadow-[0_-4px_16px_rgb(0_0_0/0.08)] after:pointer-events-none after:absolute after:-inset-x-4 after:top-full after:h-[calc(env(safe-area-inset-bottom)+16px)] after:bg-p-bg after:content-['']",
  )}>
    {reply && <div className="flex items-start justify-between gap-2 text-meta text-p-text-muted"><span>Ответ {reply.author_name}{context.parent_id ? "" : ` · ${reply.context_title}`}<br />{(reply.summary_md || reply.body_md).slice(0, 140) || "Вложение"}</span><Button variant="ghost" size="icon" onClick={onCancelReply} aria-label="Отменить ответ"><X size={16} /></Button></div>}
    {files.map((file, i) => <div key={`${file.name}-${i}`} className="flex items-center justify-between gap-2 text-meta"><span className="break-all">{file.name} · {formatFileSize(file.size)}</span><Button variant="ghost" size="icon" disabled={busy} onClick={() => setFiles((xs) => xs.filter((_, n) => n !== i))} aria-label={`Убрать ${file.name}`}><X size={16} /></Button></div>)}
    {asking && <div className="space-y-2 rounded-compact border border-p-border p-2">
      <select value={askForm.addressee_id} disabled={busy} onChange={(e) => setAsk({ addressee_id: e.target.value })} aria-label="Кого спросить" className={field}>
        <option value="">Кого спросить…</option>
        {(people.data ?? []).filter((u) => u.id !== me.data?.id).map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
      </select>
      <input value={askForm.action} maxLength={MAX_ACTION} disabled={busy} onChange={(e) => setAsk({ action: e.target.value })}
        placeholder="Что нужно: ответить или сделать" aria-label="Что нужно от человека" className={field} />
      <label className="flex min-h-touch items-center gap-2 text-meta text-p-text">
        <input type="checkbox" checked={askForm.personal} disabled={busy} onChange={(e) => setAsk({ personal: e.target.checked })} />
        Нужен личный ответ — не агента
      </label>
      <label className="flex min-h-touch items-center gap-2 text-meta text-p-text">
        <input type="checkbox" checked={askForm.blocking} disabled={busy} onChange={(e) => setAsk({ blocking: e.target.checked })} />
        Работа стоит без ответа
      </label>
      {askForm.blocking && <input value={askForm.blocking_note} maxLength={MAX_BLOCKING_NOTE} disabled={busy} onChange={(e) => setAsk({ blocking_note: e.target.value })}
        placeholder="Что именно стоит" aria-label="Что стоит без ответа" className={field} />}
    </div>}
    <button type="button" className="self-start text-meta font-semibold text-p-link" aria-expanded={asking} disabled={busy}
      onClick={() => setAsking((on) => !on)}>
      {asking ? "Без запроса" : "Запросить ответ"}
    </button>
    <input ref={picker} type="file" multiple hidden onChange={(e) => { selectFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
    <div className="flex items-end gap-2">
      <Button variant="ghost" size="icon" disabled={busy || recording} onClick={() => picker.current?.click()} aria-label="Прикрепить файл"><Paperclip size={20} /></Button>
      <div className="grow-wrap min-w-0 flex-1" data-value={draft}>
        <textarea
          rows={1}
          value={draft}
          maxLength={20000}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void send(); } }}
          placeholder={reply ? "Написать ответ…" : placeholder}
          aria-label="Сообщение в чат"
          className="rounded-[22px] border border-p-border-strong bg-p-surface text-p-text placeholder:text-p-text-muted focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-p-focus"
        />
      </div>
      {hasText
        ? <Button variant="strong" size="icon" loading={busy} disabled={recording} onClick={() => void send()} aria-label="Отправить"><Send size={18} /></Button>
        : <Button variant="ghost" size="icon" disabled={busy} onClick={() => void record()} aria-label={recording ? "Завершить запись" : "Записать голосовое"}>{recording ? <Square size={18} /> : <Mic size={20} />}</Button>}
    </div>
    {recording && <p className="text-meta text-p-text">Идёт запись, до 5 минут. Нажми на квадрат, чтобы остановить, затем «Отправить».</p>}
  </div>;
}

export interface FeedItem {
  id: string;
  /** ISO time the item happened; the feed is one line by time. */
  at: string;
  node: ReactNode;
}

export function ChatThread({
  context = {}, focusId, pinToLatest = false, extra, hideSearch = false, stickyComposer = false,
  placeholder = "Написать сообщение…",
}: {
  context?: ChatContext;
  focusId?: string;
  pinToLatest?: boolean;
  /** Spec 021: other entries of the same feed — the task's journal. */
  extra?: FeedItem[];
  hideSearch?: boolean;
  /** Spec 021: the field stays at the bottom of the screen, as on the canvas. */
  stickyComposer?: boolean;
  placeholder?: string;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [reply, setReply] = useState<ChatMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string>();
  const [editText, setEditText] = useState("");
  const [deleting, setDeleting] = useState<string>();
  const [editSummary, setEditSummary] = useState("");
  // Spec 016: messages with a short version open short; these ids are shown in full.
  // «Полностью» in Telegram leads here with the message id, so that one opens in full.
  const [full, setFull] = useState<Set<string>>(() => new Set(focusId ? [focusId] : []));
  const toggleFull = (id: string) => setFull((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const desktop = useIsDesktop();
  const [chosenOrder, setChosenOrder] = useState<ChatOrder | null>(() => storedChatOrder());
  const order: ChatOrder = pinToLatest ? "oldest" : chosenOrder ?? defaultChatOrder(desktop);
  const chooseOrder = (next: ChatOrder) => {
    setChosenOrder(next);
    try { window.localStorage.setItem(ORDER_KEY, next); } catch { /* not remembered: still applied */ }
  };
  const feed = useInfiniteQuery({
    queryKey: ["chat", context.parent_type, context.parent_id, search],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => Chat.list({ ...context, before: pageParam, q: search }),
    getNextPageParam: (page) => page.next_before ?? undefined,
    refetchInterval: 5_000,
  });
  const focused = useQuery({ queryKey: ["chat", "message", focusId], queryFn: () => Chat.get(focusId!), enabled: !!focusId });
  const scrolled = useRef<string>();
  useEffect(() => {
    if (focusId) setFull((current) => current.has(focusId) ? current : new Set(current).add(focusId));
  }, [focusId]);
  useEffect(() => {
    // Bring the message from a notification into view once it is on the page.
    if (!focusId || scrolled.current === focusId) return;
    const node = document.getElementById(`message-${focusId}`);
    if (node) { scrolled.current = focusId; node.scrollIntoView({ block: "center" }); }
  });
  const change = async (message: ChatMessage, remove: boolean) => {
    setBusy(true);
    try {
      if (remove) await Comments.remove(message.id);
      else await Comments.update(message.id, editPatch(message, editText, editSummary));
      setEditing(undefined); setDeleting(undefined);
      await qc.invalidateQueries({ queryKey: ["chat"] });
      await qc.invalidateQueries({ queryKey: ["comments"] });
    } catch (error) { toast.error(error instanceof Error ? error.message : "Не удалось изменить сообщение"); }
    finally { setBusy(false); }
  };
  const messages = [...new Map((feed.data?.pages.flatMap((p) => p.messages) ?? []).map((m) => [m.id, m])).values()]
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));

  // Task a60d4d9a: read marks — the «Новое» line and «прочитано до».
  // One discussion only: the whole feed and a search through it mark nothing.
  const oneDiscussion = Boolean(context.parent_id) && !search;
  const discussion = `${context.parent_type}:${context.parent_id}`;
  // The server's mark from this visit's own fetch, not a cached one from the last visit.
  const served = feed.isFetchedAfterMount ? feed.data?.pages[0]?.read_mark : undefined;
  // The line stays where the reader stopped when the discussion opened: marking it read
  // moves the server's mark, never the line under the reader's eyes.
  const [since, setSince] = useState<{ discussion: string; mark: ReadMark | null }>();
  useEffect(() => {
    if (served !== undefined && since?.discussion !== discussion) setSince({ discussion, mark: served });
  }, [discussion, served, since]);
  const shownMark = since?.discussion === discussion ? since.mark : served;
  const firstNew = oneDiscussion ? firstNewId(messages, shownMark, me.data?.id) : undefined;
  // Read = the discussion is open on a screen someone sees (not a tab in the background):
  // the mark moves to the newest message loaded, and again as new ones arrive.
  const [seen, setSeen] = useState(() => typeof document === "undefined" || document.visibilityState !== "hidden");
  useEffect(() => {
    const onChange = () => setSeen(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  const readTo = oneDiscussion && seen && feed.isFetchedAfterMount
    ? markTarget(messages, feed.data?.pages[0]?.read_mark)
    : undefined;
  const marked = useRef<string>();
  useEffect(() => {
    if (!readTo || marked.current === readTo) return;
    marked.current = readTo;
    Chat.read(readTo)
      .then(() => qc.invalidateQueries({ queryKey: ["chat", "threads"] }))
      .catch(() => { marked.current = undefined; /* the next poll tries again */ });
  }, [readTo, qc]);

  // ── Spec 020 D7: the newest message, «вниз», a steady screen ──────────
  const [below, setBelow] = useState(0);
  const [away, setAway] = useState(false);
  const atBottom = useRef(true);
  const newest = useRef<string>();
  const positioned = useRef(false);
  const keepFromBottom = useRef<number | null>(null);
  const older = useRef<HTMLDivElement>(null);
  const toBottom = (smooth: boolean) =>
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  const loadOlderRef = useRef(() => {});
  const loadOlder = () => {
    if (!feed.hasNextPage || feed.isFetchingNextPage) return;
    // The page grows above the reader: remember the distance from the bottom.
    keepFromBottom.current = document.documentElement.scrollHeight - window.scrollY;
    void feed.fetchNextPage();
  };
  useEffect(() => { loadOlderRef.current = loadOlder; });
  useEffect(() => {
    if (!pinToLatest) return;
    const onScroll = () => {
      const gap = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      atBottom.current = gap < NEAR_BOTTOM;
      setAway(gap > FAR_FROM_BOTTOM);
      if (atBottom.current) setBelow(0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pinToLatest]);
  const ids = messages.map((m) => m.id);
  const lastId = ids[ids.length - 1];
  useLayoutEffect(() => {
    if (!pinToLatest || !lastId) return;
    if (keepFromBottom.current !== null) {
      window.scrollTo({ top: document.documentElement.scrollHeight - keepFromBottom.current });
      keepFromBottom.current = null;
    }
    if (!positioned.current) {
      positioned.current = true;
      newest.current = lastId;
      // A link to one message («Полностью» from Telegram) centres that one instead.
      if (!focusId) toBottom(false);
      return;
    }
    const fresh = arrivals(ids, newest.current);
    newest.current = lastId;
    if (!fresh) return;
    if (atBottom.current) toBottom(true);
    else setBelow((n) => n + fresh);
    // `ids` is rebuilt every render; its length and last id are what change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinToLatest, lastId, ids.length, focusId]);
  const autoLoad = pinToLatest && mayLoadOlderByItself(focusId, ids) && feed.hasNextPage;
  useEffect(() => {
    // Older messages load as the reader scrolls up to them. Recreated only when
    // that changes — not on every key in the field (a new observer reports at
    // once and could pull a page in while you type).
    const node = older.current;
    if (!autoLoad || !node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && positioned.current) loadOlderRef.current();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoLoad]);
  const when = (iso: string) =>
    new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  // Spec 021 — a message as on the canvas: author, the «агент» badge, the time;
  // the text; quiet actions underneath instead of three buttons per message.
  const render = (message: ChatMessage) => {
    if (isSystemNotice(message)) return <SystemNotice key={message.id} message={message} inContext={Boolean(context.parent_id)} onReply={() => setReply(message)} />;
    const mine = me.data?.id === message.author_id;
    return <article key={message.id} id={`message-${message.id}`} className={cn(
      "rounded-card border px-4 py-3",
      mine ? "bg-p-surface-2" : "bg-p-surface",
      message.id === focusId ? "border-p-focus" : "border-p-border",
    )}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-body font-semibold text-p-text">{message.author_name}</span>
        {message.via_token_id && <span className="rounded-full border border-p-border-strong px-1.5 text-chip text-p-text-muted">агент</span>}
        <time dateTime={message.created_at} className="ml-auto text-meta text-p-text-muted">{when(message.created_at)}</time>
      </div>
      {message.parent_type !== "chat" && !context.parent_id && <Link to={message.context_url} className="mt-1 block text-meta text-p-link">{contextLabel(message)}</Link>}
      {message.reply_to && <blockquote className="my-2 border-l-[3px] border-p-border-strong pl-3 text-meta text-p-text-muted">↪ {message.reply_to.author_name}: {message.reply_to.summary_md || message.reply_to.body_md || "Вложение"}</blockquote>}
      {editing === message.id ? <div className="my-2 space-y-2">{message.summary_md != null && <TextArea label="Кратко" value={editSummary} maxLength={600} onChange={(e) => setEditSummary(e.target.value)} />}<TextArea label={message.summary_md != null ? "Полностью" : undefined} aria-label="Изменить сообщение" value={editText} maxLength={20000} onChange={(e) => setEditText(e.target.value)} /><div className="flex gap-2"><Button size="compact" disabled={!editText.trim() || busy || !Object.keys(editPatch(message, editText, editSummary)).length} onClick={() => void change(message, false)}>Сохранить</Button><Button size="compact" variant="ghost" onClick={() => setEditing(undefined)}>Отмена</Button></div></div> : <MessageText message={message} full={full.has(message.id)} onToggle={() => toggleFull(message.id)} />}
      {message.attachments.map((file) => <MessageFile key={file.id} file={file} />)}
      <div className="-mb-2 mt-0.5 flex flex-wrap gap-x-4 text-meta">
        <button type="button" className="min-h-touch text-p-link" onClick={() => setReply(message)}>Ответить</button>
        {mine && !editing && <>
          <button type="button" className="min-h-touch text-p-link" onClick={() => { setEditing(message.id); setEditText(message.body_md); setEditSummary(message.summary_md ?? ""); }}>Изменить</button>
          <button type="button" className="min-h-touch text-p-text-muted" disabled={busy} onClick={() => deleting === message.id ? void change(message, true) : setDeleting(message.id)}>{deleting === message.id ? "Точно удалить" : "Удалить"}</button>
          {deleting === message.id && <button type="button" className="min-h-touch text-p-link" onClick={() => setDeleting(undefined)}>Отмена</button>}
        </>}
      </div>
    </article>;
  };
  // Spec 021: the task's journal joins the discussion — one feed by time.
  const ordered = orderFeed([
    ...messages.map((m) => ({ id: m.id, at: m.created_at, node: render(m) })),
    ...(extra ?? []).map((item) => ({ ...item, node: <Fragment key={item.id}>{item.node}</Fragment> })),
  ], order);
  const newLine = { id: "new-line", at: "", node: <NewLine key="new-line" newestFirst={order === "newest"} /> };
  const feedItems = withNewLine(ordered, firstNew, order === "newest", newLine);
  // Older pages continue the feed on the side where the oldest messages are.
  const olderButton = feed.hasNextPage && <div ref={older}><Button variant="ghost" loading={feed.isFetchingNextPage} onClick={loadOlder}>Показать более ранние</Button></div>;
  const composer = <ChatComposer
    context={context}
    reply={reply}
    onCancelReply={() => setReply(null)}
    onSent={() => { setReply(null); atBottom.current = true; /* your own message: the screen follows it down */ }}
    placeholder={placeholder}
    sticky={stickyComposer}
  />;
  // A field that is not pinned to the screen goes where the newest messages are.
  const composerOnTop = order === "newest" && !stickyComposer;
  return <div className="flex flex-col gap-3">
    {!pinToLatest && ordered.length > 1 && (
      <div role="group" aria-label="Порядок сообщений" className="flex flex-wrap gap-2">
        <Chip on={order === "newest"} onClick={() => chooseOrder("newest")}>Сначала новые</Chip>
        <Chip on={order === "oldest"} onClick={() => chooseOrder("oldest")}>Сначала старые</Chip>
      </div>
    )}
    {!hideSearch && <input className="h-11 w-full rounded-compact border border-p-border bg-p-surface px-3 text-[16px] text-p-text placeholder:text-p-text-muted" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск в переписке и расшифровках" aria-label="Поиск в чате" />}
    {focused.data && !messages.some((m) => m.id === focused.data.id) && <div className="rounded-card border-2 border-p-focus p-1"><p className="p-2 text-meta text-p-text-muted">Сообщение из уведомления</p>{render(focused.data)}</div>}
    {focused.isError && <p role="alert">Сообщение из уведомления недоступно.</p>}
    {composerOnTop && composer}
    {order === "oldest" && olderButton}
    {feed.isLoading && <p className="text-p-text-muted">Загружаем переписку…</p>}
    {feed.isError && <p role="alert">Не удалось обновить чат. <Button variant="ghost" onClick={() => void feed.refetch()}>Повторить</Button></p>}
    {!feed.isLoading && !feed.isError && !ordered.length && <p className="py-6 text-center text-p-text-muted">{search ? "Сообщения не найдены" : "Начни обсуждение. Здесь сохраняются сообщения и файлы."}</p>}
    {feedItems.map((item) => item.node)}
    {order === "newest" && olderButton}
    {!composerOnTop && composer}
    {pinToLatest && (below > 0 || away) && (
      <button
        type="button"
        onClick={() => { setBelow(0); toBottom(true); }}
        aria-label={below > 0 ? `Вниз, новых: ${below}` : "Вниз, к последним"}
        className="press-surface fixed bottom-[calc(88px+env(safe-area-inset-bottom))] right-4 z-sticky flex h-11 w-11 items-center justify-center rounded-full border border-p-border bg-p-surface text-p-text shadow-[0_2px_8px_rgb(0_0_0/0.12)] lg:bottom-8"
      >
        <ChevronDown className="h-5 w-5" aria-hidden="true" />
        {below > 0 && (
          <span className="absolute -right-1 -top-1 h-[18px] min-w-[18px] rounded-full bg-p-needs px-[5px] text-center text-[11px] font-bold leading-[18px] text-p-surface">
            {below}
          </span>
        )}
      </button>
    )}
  </div>;
}
